/**
 * GetProductList Endpoint
 *
 * Fetches a list of products (travel packages) based on search criteria.
 * Used for search results and product listing pages.
 */

import type { GetProductListResult } from '@/types/schemas';
import { playerClient } from './playerClient';
import { normalizeProductListResult } from '@/services/mappers/arrayNormalizers';
import {
  buildRequestHeader,
  buildPassengersXml,
  buildPeriodXml,
  buildGeoLocationXml,
  buildGroupIdsXml,
  buildAccommodationPropertiesXml,
  type PassengerConfig,
} from '@/utils/xml/xmlBuilder';
import { PLAYER_API_CONFIG } from '@/constants/player/config';

const { NAMESPACE } = PLAYER_API_CONFIG;

/**
 * Parameters for GetProductList endpoint
 */
export interface GetProductListParams extends PassengerConfig {
  startDate: string;
  endDate: string;
  duration: number;
  durations?: number[];
  preferredDurations?: number[];
  countryCode?: string;
  groupIds?: number[];
  sortBy?: string;
  resultsFrom?: string;
  resultsTotal?: string;
  accommodationProperties?: string[];
  requestBooking?: 'Exclude';
}

/**
 * Creates SOAP request XML for GetProductList endpoint
 *
 * @param authKey - Authentication key
 * @param params - Search parameters
 * @returns SOAP envelope XML string
 */
function createGetProductListRequest(authKey: string, params: GetProductListParams): string {
  const passengersXml = buildPassengersXml({
    adults: params.adults,
    children: params.children,
  });

  const periodXml = buildPeriodXml({
    start: params.startDate,
    end: params.endDate,
    duration: params.duration,
    durations: params.durations,
    preferredDurations: params.preferredDurations,
  });

  const geoLocationXml = buildGeoLocationXml(params.countryCode);

  const groupIdsXml = buildGroupIdsXml(params.groupIds);

  const accommodationPropertiesXml = buildAccommodationPropertiesXml(
    params.accommodationProperties
  );

  const paginationXml = `
    ${params.resultsFrom ? `<bewo:ResultsFrom>${params.resultsFrom}</bewo:ResultsFrom>` : ''}
    ${params.resultsTotal ? `<bewo:ResultsTotal>${params.resultsTotal}</bewo:ResultsTotal>` : ''}
  `;

  const requestBookingXml = params.requestBooking
    ? `<bewo:RequestBooking>${params.requestBooking}</bewo:RequestBooking>`
    : '<bewo:RequestBooking xsi:nil="true"/>';

  return `
<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
                  xmlns:bewo="${NAMESPACE}"
                  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
   <soapenv:Header/>
   <soapenv:Body>
      <bewo:GetProductList>
         <bewo:GetProductListRequest>
            ${buildRequestHeader(authKey)}
            <bewo:Accommodation>
               <bewo:ProductCodes/>
               <bewo:Identities/>
               <bewo:Name xsi:nil="true"/>
               <bewo:MinCategory xsi:nil="true"/>
               <bewo:Rooms/>
               <bewo:Boards/>${accommodationPropertiesXml}
            </bewo:Accommodation>
            <bewo:Flight>
               <bewo:DepartureAirports xsi:nil="true"/>
               <bewo:ArrivalAirports xsi:nil="true"/>
               <bewo:FlightNumbers xsi:nil="true"/>
               <bewo:DepartureAirportsInbound xsi:nil="true"/>
               <bewo:ArrivalAirportsInbound xsi:nil="true"/>
            </bewo:Flight>
            <bewo:Extra xsi:nil="true"/>
            <bewo:Passengers>${passengersXml}
            </bewo:Passengers>
            ${periodXml}
            ${geoLocationXml}
            ${groupIdsXml}
            <bewo:Price xsi:nil="true"/>
            ${requestBookingXml}${paginationXml}
            <bewo:DetailLevel>PriceDetailsPerPerson</bewo:DetailLevel>
            <bewo:SortedBy>${params.sortBy || 'Price'}</bewo:SortedBy>
            <bewo:DescendingSort>false</bewo:DescendingSort>
         </bewo:GetProductListRequest>
      </bewo:GetProductList>
   </soapenv:Body>
</soapenv:Envelope>`;
}

/**
 * Calls GetProductList API endpoint
 *
 * Searches for travel packages based on criteria like dates, passengers, destination, etc.
 * Results are automatically normalized to ensure type-safe arrays.
 *
 * @param params - Search parameters (dates, passengers, destination, sorting)
 * @param options - Optional configuration (signal for request cancellation)
 * @returns Object containing raw XML response and normalized result
 * @throws {PlayerApiError} On API errors or invalid responses
 *
 * @example
 * ```typescript
 * const { result } = await getProductList({
 *   startDate: "2024-07-01",
 *   endDate: "2024-07-31",
 *   duration: 7,
 *   adults: 2,
 *   children: [],
 *   countryCode: "ES",
 *   sortBy: "Price"
 * });
 *
 * if (result?.Products?.Product) {
 *   const products = result.Products.Product; // Type-safe array
 * }
 * ```
 *
 * @example With request cancellation
 * ```typescript
 * const controller = new AbortController();
 * const { result } = await getProductList(params, {
 *   signal: controller.signal
 * });
 * // controller.abort() to cancel request
 * ```
 */
export async function getProductList(
  params: GetProductListParams,
  options?: { signal?: AbortSignal }
): Promise<{
  raw: string;
  result: GetProductListResult | null;
}> {
  const requestBody = createGetProductListRequest('', params);

  const response = await playerClient.call({
    action: 'BewotecHubService/GetProductList',
    body: requestBody,
    signal: options?.signal,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parsed = response.parsed as any;
  const rawResult = parsed?.Envelope?.Body?.GetProductListResponse?.GetProductListResult || null;

  const result = rawResult ? normalizeProductListResult(rawResult as GetProductListResult) : null;

  return { raw: response.raw, result };
}
